import {defaultDistanceType} from "@client/components/cardio/model";
import {notify} from "@client/features/notifications";
import * as hardware from "@client/hardware";
import type {Action, AppDispatch, GetState, RootState} from "@client/store";
import {server} from "@reactivated";
import {produce} from "immer";
import {P, match} from "ts-pattern";

// A session the app itself is recording. Swift owns the clock: `elapsed` is
// whatever the last metrics event (or status() on resume) reported, and
// `elapsedAt` is when it arrived, so the card can tick between events without
// ever saving a number JS counted.
export type RecordingSession =
    | {kind: "unavailable"}
    | {kind: "idle"}
    | {
          kind: "running" | "paused";
          exerciseUuid: UUID;
          startedAt: DateTimeValue;
          elapsed: SecondsValue;
          elapsedAt: number;
          heartRate: number | null;
          distance: number | null;
          energy: number | null;
      }
    | {kind: "completed"; result: hardware.WorkoutResult};

export type Actions =
    | {type: "SET_RECORDING_SESSION"; session: RecordingSession}
    | {type: "SET_RECORDING_METRICS"; metrics: hardware.WorkoutMetricsEvent}
    | {type: "SET_RECORDING_STATE"; state: "running" | "paused"};

const initialState = {
    // Until status() answers, there is no Record button anywhere.
    session: {kind: "unavailable"} as RecordingSession,
};

export const reducer = (state = initialState, action: Action): typeof initialState => {
    switch (action.type) {
        case "SET_RECORDING_SESSION": {
            return {...state, session: action.session};
        }
        case "SET_RECORDING_METRICS": {
            if (state.session.kind != "running" && state.session.kind != "paused") {
                return state;
            }
            return produce(state, (draft) => {
                if (draft.session.kind != "running" && draft.session.kind != "paused") {
                    return;
                }
                draft.session.elapsed = action.metrics.elapsed_seconds;
                draft.session.elapsedAt = Date.now();
                draft.session.heartRate = action.metrics.heart_rate;
                draft.session.distance = action.metrics.distance_meters;
                draft.session.energy = action.metrics.energy_kcal;
            });
        }
        case "SET_RECORDING_STATE": {
            if (state.session.kind != "running" && state.session.kind != "paused") {
                return state;
            }
            return produce(state, (draft) => {
                if (draft.session.kind != "running" && draft.session.kind != "paused") {
                    return;
                }
                draft.session.kind = action.state;
            });
        }
        default: {
            return state;
        }
    }
};

export const getSession = (state: RootState) => state.recording.session;

const sessionFromStatus = (status: hardware.WorkoutStatus): RecordingSession =>
    match(status)
        .with({state: "unsupported"}, () => ({kind: "unavailable"}) as const)
        .with({state: "idle"}, () => ({kind: "idle"}) as const)
        .with({state: "completed", result: P.nonNullable}, (completed) => ({
            kind: "completed" as const,
            result: completed.result,
        }))
        .with(
            {
                state: P.union("running", "paused"),
                exercise_uuid: P.nonNullable,
                started_at: P.nonNullable,
            },
            (live) => ({
                kind: live.state,
                exerciseUuid: live.exercise_uuid,
                startedAt: live.started_at,
                elapsed: live.elapsed_seconds ?? 0,
                elapsedAt: Date.now(),
                heartRate: live.heart_rate ?? null,
                distance: live.distance_meters ?? null,
                energy: live.energy_kcal ?? null,
            }),
        )
        // A running session with no exercise, or a completed one with no result,
        // is not a session anyone can act on.
        .otherwise(() => ({kind: "idle"}) as const);

export const refreshSession = () => async (dispatch: AppDispatch) => {
    const status = await hardware.getWorkoutStatus();
    const session: RecordingSession =
        status.status == "not_implemented"
            ? {kind: "unavailable"}
            : sessionFromStatus(status.data);

    dispatch({type: "SET_RECORDING_SESSION", session});

    return session;
};

// The one place a finished session is saved: boot, resume and the end event all
// come through here.
export const syncStatus = () => async (dispatch: AppDispatch) => {
    const session = await dispatch(refreshSession());

    if (session.kind == "completed") {
        // oxlint-disable-next-line typescript/no-use-before-define
        await dispatch(finalize(session.result));
    }
};

let listening = false;

export const initialize = () => (dispatch: AppDispatch) => {
    if (listening == true) {
        return;
    }
    listening = true;

    void hardware.addWorkoutMetricsListener((metrics) => {
        dispatch({type: "SET_RECORDING_METRICS", metrics});
    });

    void hardware.addWorkoutStateListener((event) => {
        if (event.state == "running" || event.state == "paused") {
            dispatch({type: "SET_RECORDING_STATE", state: event.state});
            return;
        }
        void dispatch(syncStatus());
    });

    // The webview is suspended while the phone is locked, so resume is where the
    // truth is re-read — including a session that ended while we were away.
    void hardware.App.addListener("appStateChange", (appState) => {
        if (appState.isActive == true) {
            void dispatch(syncStatus());
        }
    });

    void dispatch(syncStatus());
};

export const startRecording =
    (options: {
        exerciseUuid: UUID;
        activity: string;
        environment: hardware.WorkoutEnvironment;
    }) =>
    async (dispatch: AppDispatch, getState: GetState) => {
        // Asked here, on the first Record, not at Apple Health enable: the
        // session writes workouts and routes and reads heart rate.
        await hardware.HealthPlugin.requestAuthorization({
            all: [],
            read: [
                "heartRate",
                "activeEnergyBurned",
                "distanceWalkingRunning",
                "distanceCycling",
                "distanceSwimming",
            ],
            // Indoors the treadmill's distance is written back as the workout's.
            write:
                options.environment === "INDOOR"
                    ? ["workoutType", "workoutRoute", "distanceWalkingRunning"]
                    : ["workoutType", "workoutRoute"],
        }).catch(() => undefined);

        // Without share access the session would run and fail at End, when the
        // workout cannot be saved. Sharing is the one status HealthKit reports;
        // a status that cannot be read counts as denied.
        const sharing = await hardware.HealthPlugin.authorizationStatus({
            sampleType: "workoutType",
        }).catch(() => ({status: "sharingDenied" as const}));
        if (sharing.status === "sharingDenied") {
            dispatch(
                notify("Joy can't save workouts.\nAllow it in Settings, then Health.", {
                    type: "error",
                }),
            );
            return;
        }

        const started = await hardware.startWorkout({
            exercise_uuid: options.exerciseUuid,
            activity: options.activity,
            environment: options.environment,
            // For METs on the HealthKit workout; the phone never reads body mass.
            weight_kg: getState().orm.profile?.latest_weight_kilograms ?? null,
        });

        if (started.status == "not_implemented") {
            dispatch({type: "SET_RECORDING_SESSION", session: {kind: "unavailable"}});
            return;
        }

        await dispatch(syncStatus());
    };

export const pauseRecording = () => async (dispatch: AppDispatch) => {
    await hardware.pauseWorkout();
    await dispatch(syncStatus());
};

export const resumeRecording = () => async (dispatch: AppDispatch) => {
    await hardware.resumeWorkout();
    await dispatch(syncStatus());
};

export const endRecording = () => async (dispatch: AppDispatch) => {
    await hardware.endWorkout();
    await dispatch(syncStatus());
};

// Completing a workout ends a session recording into it and saves it first, so
// the recorded set is in place before the workout is saved and closed.
export const endRecordingIn =
    (exerciseUuids: UUID[]) => async (dispatch: AppDispatch, getState: GetState) => {
        const session = getSession(getState());
        if (
            (session.kind === "running" || session.kind === "paused") &&
            exerciseUuids.includes(session.exerciseUuid)
        ) {
            await dispatch(endRecording());
        }
    };

// The server writes a recording's values, so the save hands them back and the
// card takes them if it is on screen. Nothing is kept for a card that is not:
// the next load reads the server.
type RecordedExercise = server.journal.workouts.RecordedExercise.output;

const savedListeners = new Map<UUID, (saved: RecordedExercise) => void>();

export const onSaved = (uuid: UUID, listener: (saved: RecordedExercise) => void) => {
    savedListeners.set(uuid, listener);
    return () => {
        if (savedListeners.get(uuid) === listener) {
            savedListeners.delete(uuid);
        }
    };
};

const pingOrphaned = async (result: hardware.WorkoutResult) => {
    const {deviceUUID} = await hardware.getAppInfo();
    void server.core.rpc.apple_ping({
        device_id: deviceUUID,
        type: "RECORDING_ORPHANED",
        data: result,
    });
};

// One finalization at a time: a resume landing mid-post must not send it twice.
let finalizing: Promise<void> | null = null;

// The plugin holds the recording until the server has it. Anything short of an
// answer leaves it there, and the next resume retries, because syncStatus runs
// on every resume.
export const finalize =
    (result: hardware.WorkoutResult) =>
    async (dispatch: AppDispatch, getState: GetState) => {
        if (finalizing != null) {
            return finalizing;
        }

        finalizing = (async () => {
            // Meters from the phone; the set's unit is the profile's, converted once
            // on the server.
            const saved = await server.journal.workouts
                .record_exercise_result({
                    ...result,
                    distance_type: defaultDistanceType(
                        getState().orm.profile?.is_imperial ?? true,
                    ),
                })
                .catch(() => null);

            if (saved?.type != "success") {
                return;
            }

            // No exercise for this result: it stays in Health, where nothing is
            // lost, and out of Joy, where its source hides it. Never deleted here.
            if (saved.data == null) {
                await pingOrphaned(result);
            } else {
                // The card takes the values before the session goes idle, so it
                // never shows a set that is neither recording nor recorded.
                savedListeners.get(saved.data.uuid)?.(saved.data);
            }

            await hardware.acknowledgeWorkout();
            await dispatch(refreshSession());
        })();

        try {
            await finalizing;
        } finally {
            finalizing = null;
        }
    };

// Logout's one attempt while the credentials are still this account's; a
// result the post leaves behind stays in the plugin for the next login.
export const flushCompleted = () => async (dispatch: AppDispatch) => {
    const session = await dispatch(refreshSession());

    if (session.kind == "completed") {
        await dispatch(finalize(session.result));
    }
};

// What the card shows: Swift's last reading plus the seconds since it arrived
// while running, frozen while paused. Display only; the saved value is Swift's.
export const displayElapsed = (
    session: Extract<RecordingSession, {kind: "running" | "paused"}>,
    now: number,
): SecondsValue =>
    session.kind === "running"
        ? session.elapsed + Math.max(0, now - session.elapsedAt) / 1000
        : session.elapsed;
